import { RoundedInputField } from '@/shared/ui/RoundedInputField'
import { Box, Popper, useTheme } from '@mui/material'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import React, { useRef } from 'react'
import SearchBarIcons from '../../../shared/ui/SearchBarIcons'
import { useTranslation } from 'react-i18next'
import { minCharSearchLimit } from '@/shared/constants/appConstants'
import { useSearchBarLogic } from '../hooks/useSearchBarLogic'
import { PopperSearchResult } from '@/features/SearchBar/ui/components/PopperSearchResults'
import { useFocusable } from '@noriginmedia/norigin-spatial-navigation'
import {
    DIRECTION_UP_FOCUS_KEY,
    SEARCH_INPUT_FOCUS_KEY,
} from '@/shared/constants/focusLibraryConstants'
import { isSmartTV } from '@/shared/lib'

const SearchBar = (): JSX.Element => {
    const { t: translate } = useTranslation()
    const theme = useTheme()
    const inputRef = useRef<HTMLInputElement>(null)

    const {
        handlePopperClick,
        handlePopperClose,
        handleInputChange,
        anchorEl,
        searchValue,
        setSearchValue,
        channelsData,
        open,
        id,
        isUserLoggedIn,
    } = useSearchBarLogic()

    const popperOffset = [42, 12]

    const popperContainerStyle = {
        padding: '24px 24px 32px 24px',
        backgroundColor: 'custom.midGray',
        zIndex: 9,
        width: 'max-content',
        boxShadow: '0px 4px 39px 0px #00000040',
        borderRadius: theme.shape.borderRadius,
        top: '16px',
    }

    const searchResultContainerStyle = {
        maxHeight: 'unset',
    }

    const popperModifiers = [
        {
            name: 'offset',
            options: {
                offset: popperOffset,
            },
        },
        {
            name: 'preventOverflow',
            options: {
                padding: 30,
                boundariesElement: 'viewport',
            },
        },
    ]

    const containerStyle = {
        width: '100vw',
        maxWidth: { xs: '100px', sm: '200px', md: '385px' },
    }

    const { ref: searchRef, focused } = useFocusable({
        focusKey: SEARCH_INPUT_FOCUS_KEY,

        focusable: true,
        isFocusBoundary: true,
        focusBoundaryDirections: [DIRECTION_UP_FOCUS_KEY],

        onBlur: () => {
            inputRef?.current?.blur()
        },

        onFocus: () => {
            inputRef?.current?.focus()
        },
    })

    return (
        <ClickAwayListener onClickAway={handlePopperClose}>
            <Box
                sx={containerStyle}
                ref={searchRef}
                border={focused && isSmartTV() ? '2px solid red' : ''}>
                <RoundedInputField
                    placeholder={translate('search')}
                    tooltip={
                        searchValue.length < minCharSearchLimit
                            ? translate('searchInputTooltip')
                            : ''
                    }
                    isCharactersLimit={true}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    icons={<SearchBarIcons />}
                    onChange={handleInputChange}
                    onClick={handlePopperClick}
                    disabled={!isUserLoggedIn}
                    native={{ ref: inputRef }}
                />
                {searchValue.length >= minCharSearchLimit && (
                    <Popper
                        id={id}
                        open={!!searchValue && open}
                        anchorEl={anchorEl}
                        sx={popperContainerStyle}
                        placement="bottom-end"
                        modifiers={popperModifiers}>
                        <Box sx={searchResultContainerStyle}>
                            <PopperSearchResult
                                channels={channelsData}
                                query={searchValue}
                                onClick={handlePopperClose}
                            />
                        </Box>
                    </Popper>
                )}
            </Box>
        </ClickAwayListener>
    )
}

export { SearchBar }
